import clsx from 'clsx';
import PropTypes from 'prop-types';
import {useProductOptions, ClientAnalytics} from '@shopify/hydrogen';
import {useEffect, useState} from 'react';

import {YotpoStars} from '../../yotpo/YotpoStars.client';
import {ContentModal} from '~/components/elements/ContentModal.client';
import {Button} from '~/components/global/Button.client';

import {useImageOptimizer} from '~/hooks/useImageOptimizer';
import {useIsMobile} from '~/hooks/useIsMobile';
import {useModal} from '~/hooks/useModal';
import {useParamHelper} from '~/hooks/useParamHelper';
import {intellisuggestClickTracking} from '~/utils/intellisuggestClickTracking';
import {trackClickEvent} from '~/utils/trackClickEvent';

/**
 * ProductGridCard component
 */

export function ProductGridCard({
  index,
  btnCtaText,
  btnVariant,
  href,
  imageOverride,
  variantOverride,
  pricePreview,
  product,
  intellisuggestData,
  intellisuggestSignature,
  children,
}) {
  const {isVisible, toggleVisibility} = useModal();
  const isMobile = useIsMobile();
  const {scrubNonManualParams} = useParamHelper();

  // NOTE: URL state changes seem to act differently in different environments
  // (run dev vs preview or hosted)
  const onClick = (e) => {
    e.preventDefault();

    scrubNonManualParams('filter.');

    toggleVisibility();
  };

  const [activeVariantUrl, setActiveVariantUrl] = useState(href);

  const handleCardClick = (e) => {
    intellisuggestClickTracking(e, intellisuggestData, intellisuggestSignature);
    const closestH1 = document.querySelector('h1');
    const item_list_name = closestH1 ? closestH1.textContent.trim() : '';
    trackClickEvent('select_item', {product, index, item_list_name, e});
  };

  const handleButtonClick = (e) => {
    const closestH1 = document.querySelector('h1');
    const item_list_name = closestH1 ? closestH1.textContent.trim() : '';
    trackClickEvent('select_item', {product, index, item_list_name, e});
  };

  const cardImage = useImageOptimizer(
    imageOverride ?? product?.store?.previewImageUrl,
    400,
  );

  return (
    <div className="grid-card">
      {renderGridCard(
        href,
        cardImage,
        product,
        pricePreview,
        variantOverride,
        handleCardClick,
        activeVariantUrl,
        setActiveVariantUrl,
      )}

      {product?.store?.reviews !== false && (
        <div className="yotpo-stars">
          <YotpoStars sanityProduct={product.store} />
        </div>
      )}
      <div className="btn-wrapper">
        <Button
          variant={btnVariant}
          ctaText={btnCtaText}
          href={activeVariantUrl}
          onClick={(e) => handleButtonClick(e)}
        />
      </div>
    </div>
  );
}

function generateVariantLink(productTitle, variantTitle) {
  const formatTitle = (title) => title.toLowerCase().replace(/\s+/g, '-');
  const formattedProductTitle = formatTitle(productTitle);
  const formattedVariantTitle = formatTitle(variantTitle);

  return formattedVariantTitle
    ? `/products/${formattedProductTitle}-${formattedVariantTitle}`
    : `/products/${formattedProductTitle}`;
}
function renderGridCard(
  href,
  cardImage,
  product,
  pricePreview,
  variantOverride,
  handleCardClick,
  activeVariantUrl,
  setActiveVariantUrl,
) {
  const {selectedVariant} = useProductOptions();
  const [activeVariant, setActiveVariant] = useState(selectedVariant);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverImage, setHoverImage] = useState('');
  const [hoverEffectEnabled, setHoverEffectEnabled] = useState(false);

  const getImagePath = (imageUrl) => {
    try {
      return new URL(imageUrl, window.location.origin).pathname;
    } catch (error) {
      console.error('Error constructing image URL:', error);
      return null;
    }
  };

  useEffect(() => {
    const updateHoverImage = () => {
      let hoverImageUrl = '';
      let isHoverEnabled = false;
      // Check if variantOverride is provided and has the required properties
      if (
        variantOverride &&
        variantOverride.enableHover &&
        variantOverride.images?.[1]
      ) {
        hoverImageUrl = variantOverride.images[1].asset.url;
        isHoverEnabled = true;
      } else {
        // Find the variant with an image that matches cardImage
        const matchingVariant = product.store.variants.find((variant) => {
          const variantImagePath = getImagePath(variant.store?.previewImageUrl);
          const cardImagePath = getImagePath(cardImage?.optimizedImageSrc);
          return variantImagePath === cardImagePath;
        });
        // If a matching variant is found and it has hover enabled, use its second image
        if (matchingVariant?.enableHover && matchingVariant.images?.[1]) {
          hoverImageUrl = matchingVariant.images[1].asset.url;
          isHoverEnabled = true;
        }
      }
      // Set the hover image if a suitable image URL is found
      setHoverImage(hoverImageUrl);
      setHoverEffectEnabled(isHoverEnabled);
    };
    updateHoverImage();
  }, [product.store.variants, variantOverride]);

  const handleSwatchClick = (variant) => {
    setActiveVariant(variant);
    // Use generateVariantLink to create the URL for the clicked variant
    const variantLink = generateVariantLink(
      product?.store?.slug?.current,
      variant?.store?.title,
    );
    setActiveVariantUrl(variantLink);
    const closestH1 = document.querySelector('h1');
    const item_list_name = closestH1 ? closestH1.textContent.trim() : '';
    ClientAnalytics.publish('select_item', true, {
      product,
      variant,
      item_list_name,
    });

    // Check if the clicked variant has enableHover and update the hoverImage accordingly
    if (variant.enableHover === true && variant.images?.[1]) {
      setHoverImage(variant.images[1].asset.url);
      setHoverEffectEnabled(true);
    } else {
      setHoverImage('');
      setHoverEffectEnabled(false);
    }
  };
  const isMultiplePricing =
    product.store.priceRange.minVariantPrice !==
    product.store.priceRange.maxVariantPrice;

  const displayPrice = isMultiplePricing ? (
    <span>
      <span className="price-from">from </span>
      {moneyFormatter.format(product.store.priceRange.minVariantPrice)}
    </span>
  ) : (
    moneyFormatter.format(pricePreview)
  );
  return (
    <div className="grid-card__image-wrapper">
      {href ? (
        <>
          <div
            className={`quickview__wrapper ${
              hoverEffectEnabled ? 'apply-hover-effect' : ''
            }`}
            onMouseEnter={
              hoverEffectEnabled ? () => setIsHovering(true) : undefined
            }
            onMouseLeave={
              hoverEffectEnabled ? () => setIsHovering(false) : undefined
            }
          >
            <a href={activeVariantUrl} onClick={(e) => handleCardClick(e)}>
              <img
                alt={
                  isHovering && hoverImage
                    ? 'Hover Product Image'
                    : 'Product Preview'
                }
                src={
                  isHovering && hoverImage
                    ? hoverImage
                    : activeVariant?.store?.previewImageUrl ||
                      cardImage?.optimizedImageSrc
                }
                loading="lazy"
              />
            </a>
          </div>
          <h4>
            <a href={activeVariantUrl} onClick={(e) => handleCardClick(e)}>
              {product?.store?.title}
            </a>
          </h4>
        </>
      ) : (
        <>
          <div className="quickview__wrapper">
            <img
              alt="Product preview"
              src={cardImage?.optimizedImageSrc}
              loading="lazy"
            />
          </div>
          <h4>{product?.store?.title}</h4>
        </>
      )}
      {product?.colorSwatchPicker && (
        <div className="color-swatch-picker">
          {product?.store?.variants?.map((variant, index) => {
            // Determine if this variant is selected
            const isSelected = activeVariant === variant;
            const swatchClass = isSelected
              ? 'color-swatch selected'
              : 'color-swatch';
            return (
              <div
                key={index}
                className="color-swatch-container"
                data-variant={variant?.store?.title}
              >
                <div className={swatchClass}>
                  <div className="color-container">
                    {variant.colorSwatch?.map((swatch, swatchIndex) => {
                      let style = {};
                      if (swatch._type === 'color') {
                        // If the swatch is a color, set the backgroundColor
                        style.backgroundColor = swatch.hex;
                      } else if (swatch._type === 'image') {
                        const imageUrl = useImageOptimizer(
                          swatch.asset.url,
                          50,
                        );
                        // If the swatch is an image, set the backgroundImage
                        style.backgroundImage = `url(${imageUrl.optimizedImageSrc})`;
                        style.backgroundSize = 'cover';
                      }
                      return (
                        <button
                          key={swatchIndex}
                          className="color"
                          style={style}
                          onClick={() => handleSwatchClick(variant)}
                        ></button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {atLeastOneProductVariantIsAvailableForSale(product) ? (
        pricePreview ? (
          <h6>
            {displayPrice}{' '}
            {selectedVariant?.compareAtPriceV2 &&
              selectedVariant?.priceV2?.amount !==
                selectedVariant?.compareAtPriceV2?.amount && (
                <span className="product-detail__price--compare-at">
                  {moneyFormatter.format(
                    selectedVariant?.compareAtPriceV2?.amount,
                  )}
                </span>
              )}
          </h6>
        ) : null
      ) : (
        <h6>Out of Stock</h6>
      )}
    </div>
  );
}

const atLeastOneProductVariantIsAvailableForSale = (product) => {
  return product?.matchingShopifyProduct?.variants?.nodes?.some(
    (variant) => variant.availableForSale,
  );
};

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

ProductGridCard.propTypes = {
  btnCtaText: PropTypes.string.isRequired,
  btnVariant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'dark']),
  href: PropTypes.string.isRequired,
  imageOverride: PropTypes.string,
  pricePreview: PropTypes.string,
  product: PropTypes.object.isRequired,
  intellisuggestData: PropTypes.string,
  intellisuggestSignature: PropTypes.string,
};
